import React, { useState } from "react"
import {
	ThemeProvider,
	Dialog,
	DialogContent,
	Grid,
	Typography,
	Button,
	CircularProgress,
	Box
} from "@material-ui/core"

import theme from "@/styles/theme"

import { SparkleSingleIcon } from "@/assets/icons"

import { SvgIcon, Notification } from "@/components"

import { renderComponent } from "@/utils/node"
import { whatsappLinks } from "@/utils/link"

import AttendanceProtocolSummaryService from "@/services/AttendanceProtocolSummary"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"

import { getAttendanceSummaryDialogVariantFromUserRoleName } from "@/utils/attendanceSummary"

import useStyles from "@/components/Skynet/AttendanceSummary/Upsell/AttendanceSummaryUpsellDialog/styles"

import { colors } from "@/styles/colors"

import { AttendanceSummaryDialogVariant, AttendanceSummaryDialogSource } from "@/protocols/attendanceSummary"
import { UserInInstanceRoleName } from "@/protocols/userInInstance"

type VariantToBenefits = Record<AttendanceSummaryDialogVariant, string[]>

type AttendanceSummaryUpsellDialogProps = {
	userRoleName: UserInInstanceRoleName
	source: AttendanceSummaryDialogSource
	onClose?: () => void
}

type AttendanceSummaryUpsellDialogType = {
	open: (props: AttendanceSummaryUpsellDialogProps) => void
}

const DIALOG_TITLE = "Resumo de atendimentos com IA"
const ATTENDANCE_SUMMARY_UPSELL_DIALOG_RENDER_KEY = "attendance-summary-upsell-dialog"
const DESCRIPTION_BY_SOURCE: Record<AttendanceSummaryDialogSource, Record<AttendanceSummaryDialogVariant, string>> = {
	inbox: {
		attendant: "Com o adicional de IA, qualquer atendimento pode ser resumido em segundos, com próximos passos e avaliação de qualidade automáticos.",
		manager: "Com o adicional de IA, qualquer atendimento pode ser resumido em segundos, com próximos passos, avaliação de qualidade e insights comerciais no Playground de IA."
	},
	dashboard: {
		attendant: "Com o adicional de IA, qualquer atendimento pode ser resumido em segundos, com próximos passos e avaliação de qualidade automáticos.",
		manager: "Seu atendente pode estar perdendo oportunidades comerciais, já que tentou usar o recurso de Resumo de Atendimento com IA, porém ele ainda não está habilitado na sua instância. Ative o adicional de IA e tenha próximos passos, avaliação de qualidade e insights comerciais com IA."
	}
}

const VARIANT_TO_BENEFITS: VariantToBenefits = {
	attendant: [
		"Resuma qualquer atendimento com um clique",
		"Próximos passos e qualidade automáticos"
	],
	manager: [
		"Resuma qualquer atendimento com um clique",
		"Próximos passos e qualidade automáticos",
		"Playground de IA com insights comerciais"
	]
}

const VARIANT_TO_PRIMARY_CTA_LABEL: Record<AttendanceSummaryDialogVariant, string> = {
	attendant: "Notificar seu gestor",
	manager: "Falar com Vendas"
}

const ATTENDANT_SUCCESS_TOAST_MESSAGE = "Seu gestor foi notificado!"

const AttendanceSummaryUpsellDialog: AttendanceSummaryUpsellDialogType & React.FC<AttendanceSummaryUpsellDialogProps> = (props) => {
	const {
		userRoleName,
		source,
		onClose
	} = props

	const variant = getAttendanceSummaryDialogVariantFromUserRoleName(userRoleName)

	const [isOpen, setIsOpen] = useState(true)
	const [isActionLoading, setIsActionLoading] = useState(false)

	const classes = useStyles()

	const benefits = VARIANT_TO_BENEFITS[variant]
	const primaryCtaLabel = VARIANT_TO_PRIMARY_CTA_LABEL[variant]
	const description = DESCRIPTION_BY_SOURCE[source][variant]

	const handleClose = () => {
		setIsOpen(false)
		onClose?.()
	}

	const handlePrimaryAction = async () => {
		const VARIANT_TO_PRIMARY_ACTION: Record<AttendanceSummaryDialogVariant, () => Promise<void>> = {
			attendant: async () => {
				setIsActionLoading(true)
				try {
					await AttendanceProtocolSummaryService.notifyManager()
					Notification.success({
						message: ATTENDANT_SUCCESS_TOAST_MESSAGE
					})
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
				}
				setIsActionLoading(false)
			},
			manager: async () => {
				window.open(whatsappLinks.sendMessageToAcquireAdditionalAIResources, "_blank")
			}
		}

		await VARIANT_TO_PRIMARY_ACTION[variant]()
		handleClose()
	}

	return (
		<ThemeProvider theme={theme}>
			<Dialog
				open={isOpen}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				classes={{ paper: classes.dialogPaper }}
			>
				<DialogContent className={classes.dialogContent}>
					<Grid
						container
						spacing={3}
					>
						<Grid item xs={12}>
							<Grid container alignItems="center" justifyContent="center">
								<Box className={classes.iconSquare}>
									<SvgIcon
										className={classes.icon}
										icon={SparkleSingleIcon}
										htmlColor={colors.gray[0]}
										fontSize="large"
										autoSize
									/>
								</Box>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Grid container alignItems="center" justifyContent="center">
								<Typography className={classes.title}>
									{DIALOG_TITLE}
								</Typography>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Grid container alignItems="center" justifyContent="center">
								<Typography variant="h1" className={classes.description}>
									{description}
								</Typography>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Grid container alignItems="center" justifyContent="center">
								<Box className={classes.benefitsCard}>
									<Grid
										container
									>
										{benefits.map((benefit) => (
											<Grid
												key={benefit}
											>
												<Typography
													className={classes.benefitItemText}
												>
													✓ {benefit}
												</Typography>
											</Grid>
										))}
									</Grid>
								</Box>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Grid container justifyContent="center" alignItems="center" spacing={1}>
								<Grid item>
									<Button
										variant="text"
										onClick={handleClose}
										className={classes.secondaryButton}
										disableFocusRipple
									>
										Agora não
									</Button>
								</Grid>
								<Grid item style={{ flex: 1 }}>
									<Button
										className={classes.primaryButton}
										variant="contained"
										disabled={isActionLoading}
										endIcon={isActionLoading ? <CircularProgress size={16} color="inherit" /> : null}
										onClick={handlePrimaryAction}
										fullWidth
										disableFocusRipple
									>
										{primaryCtaLabel}
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
			</Dialog>
		</ThemeProvider>
	)
}

AttendanceSummaryUpsellDialog.open = (props: AttendanceSummaryUpsellDialogProps) => {
	renderComponent(
		ATTENDANCE_SUMMARY_UPSELL_DIALOG_RENDER_KEY,
		<AttendanceSummaryUpsellDialog
			{...props}
		/>
	)
}

export default AttendanceSummaryUpsellDialog
